// sheet.js — dauerhaftes (nicht modales) Bottom-Sheet mit Rastpunkten, gezogen per Federphysik.
//   const s = createStandardSheet(el, { snaps: (h) => [peek, half, full], initial: 1, onChange })
//   s.snapTo(i) · s.index · s.destroy()
// Ziehen am Griff/Kopf; in der Liste erst, wenn sie oben ist (wie Google Maps).
import { springStep, springAtRest } from '../motion/spring-math.js';
import springs from '../tokens/springs.json' with { type: 'json' };
import { reducedMotion } from '../core/dom.js';

export function nearestSnap(y, v, snaps) {
  // y = sichtbare Höhe; v = Geschwindigkeit (px/s, positiv = nach oben); Schwung wählt die nächste Stufe in Richtung
  const projected = y + v * 0.18;
  let best = 0;
  for (let i = 1; i < snaps.length; i++) if (Math.abs(snaps[i] - projected) < Math.abs(snaps[best] - projected)) best = i;
  return best;
}

export function createStandardSheet(el, { snaps, initial = 1, onChange, handle = el.querySelector('[data-sheet-drag]'), scroller = el.querySelector('[data-sheet-scroll]') } = {}) {
  const S = springs['spatial-default'];
  let H = 0, points = [], index = initial, visible = 0, raf = 0;
  const measure = () => {
    H = el.parentElement.clientHeight;
    points = snaps(H).map((p) => Math.max(0, Math.min(H, p)));
  };
  const apply = (h) => {
    visible = h;
    el.style.setProperty('--sheet-h', Math.round(h) + 'px');
    if (el.parentElement) el.parentElement.style.setProperty('--sheet-h', Math.round(h) + 'px');
    el.style.transform = `translateY(${Math.round(H - h)}px)`;
    el.dataset.snap = String(index);
  };
  const animateTo = (target, v0 = 0) => {
    cancelAnimationFrame(raf);
    if (reducedMotion()) { apply(target); return; }
    let st = { x: visible, v: v0 }, t0 = performance.now();
    const step = (t) => {
      st = springStep(st, target, S.zeta, S.stiffness, Math.min(0.05, (t - t0) / 1000)); t0 = t;
      apply(st.x);
      if (springAtRest(st, target)) { apply(target); return; }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  };
  const snapTo = (i, v0 = 0) => {
    measure();
    const prev = index;
    index = Math.max(0, Math.min(points.length - 1, i));
    el.dataset.snap = String(index);
    animateTo(points[index], v0);
    if (onChange && prev !== index) onChange(index);
  };

  // Ziehen
  let drag = null;
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const fromList = scroller && scroller.contains(e.target);
    if (fromList && scroller.scrollTop > 0) return; // Liste scrollt zuerst
    if (fromList && e.pointerType === 'mouse') return; // Maus zieht nur am Griff
    drag = { y0: e.clientY, h0: visible, lastY: e.clientY, lastT: performance.now(), v: 0, moved: false, fromList, id: e.pointerId };
    cancelAnimationFrame(raf);
  };
  const move = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dy = e.clientY - drag.y0;
    if (!drag.moved) {
      if (Math.abs(dy) < 6) return;
      // In der Liste nach oben wischen bei voller Höhe = scrollen, nicht ziehen
      if (drag.fromList && dy < 0 && index === points.length - 1) { drag = null; return; }
      drag.moved = true;
      el.setPointerCapture?.(e.pointerId);
    }
    e.preventDefault();
    const now = performance.now();
    drag.v = -((e.clientY - drag.lastY) / Math.max(1, now - drag.lastT)) * 1000;
    drag.lastY = e.clientY; drag.lastT = now;
    let h = drag.h0 - dy;
    const min = points[0], max = points[points.length - 1];
    if (h > max) h = max + (h - max) / 4; else if (h < min) h = min - (min - h) / 4;
    apply(h);
  };
  const up = (e) => {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag; drag = null;
    if (!d.moved) return;
    const i = nearestSnap(visible, d.v, points);
    snapTo(i, d.v);
  };
  el.addEventListener('pointerdown', down);
  addEventListener('pointermove', move, { passive: false });
  addEventListener('pointerup', up);
  addEventListener('pointercancel', up);
  if (handle) {
    handle.addEventListener('click', () => snapTo(index === points.length - 1 ? 1 : index + 1));
    handle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); snapTo(index + 1); }
      if (e.key === 'ArrowDown') { e.preventDefault(); snapTo(index - 1); }
    });
  }
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { measure(); apply(points[index]); }) : null;
  if (ro) ro.observe(el.parentElement);
  measure(); apply(points[index]);

  return {
    get index() { return index; },
    snapTo,
    destroy() {
      cancelAnimationFrame(raf); if (ro) ro.disconnect();
      el.style.transform = ''; el.style.removeProperty('--sheet-h'); delete el.dataset.snap;
      if (el.parentElement) el.parentElement.style.removeProperty('--sheet-h');
      el.removeEventListener('pointerdown', down);
      removeEventListener('pointermove', move); removeEventListener('pointerup', up); removeEventListener('pointercancel', up);
    },
  };
}
